import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

/**
 * HeroOrb — Premium glowing AI orb with orbiting rings.
 * Pure Three.js for maximum compatibility.
 * Color: #F87217 orange with intense bloom-like glow.
 */
export default function HeroOrb({ className = '', style = {} }) {
    const mountRef = useRef(null);

    useEffect(() => {
        const container = mountRef.current;
        if (!container) return;

        /* ── Renderer ────────────────────────────────────── */
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        renderer.setClearColor(0x000000, 0);
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;
        container.appendChild(renderer.domElement);

        /* ── Scene & Camera ──────────────────────────────── */
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
        camera.position.set(0, 0, 6.0);

        /* ── Lights — warm orange-tinted ─────────────────── */
        scene.add(new THREE.AmbientLight(0xfff5eb, 0.5));

        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(5, 5, 5);
        scene.add(dirLight);

        // Main orange glow light
        const glow1 = new THREE.PointLight(0xF87217, 4, 10, 1.5);
        glow1.position.set(0, 0, 2.5);
        scene.add(glow1);

        // Warm fill from below
        const glow2 = new THREE.PointLight(0xFF9A4E, 2.5, 8, 1.5);
        glow2.position.set(-2, -1.5, 1);
        scene.add(glow2);

        // Cool rim for depth contrast
        const glow3 = new THREE.PointLight(0xFF6B00, 1.8, 7, 1.5);
        glow3.position.set(2, 1.5, -1);
        scene.add(glow3);

        /* ── Core orb — deep orange emissive ─────────────── */
        const orbGeo = new THREE.SphereGeometry(0.9, 64, 64);
        const orbMat = new THREE.MeshPhysicalMaterial({
            color: 0xF87217,
            emissive: 0xF87217,
            emissiveIntensity: 0.7,
            roughness: 0.08,
            metalness: 0.95,
            clearcoat: 1.0,
            clearcoatRoughness: 0.05,
        });
        const orb = new THREE.Mesh(orbGeo, orbMat);
        scene.add(orb);

        /* ── Glow shells — layered soft bloom ─────────────── */
        function addGlowShell(radius, opacity, color) {
            const geo = new THREE.SphereGeometry(radius, 32, 32);
            const mat = new THREE.MeshBasicMaterial({
                color,
                transparent: true,
                opacity,
                side: THREE.BackSide,
                depthWrite: false,
            });
            const mesh = new THREE.Mesh(geo, mat);
            scene.add(mesh);
            return mesh;
        }

        const glowShell1 = addGlowShell(1.05, 0.25, 0xF87217);
        const glowShell2 = addGlowShell(1.22, 0.12, 0xFF9A4E);
        const glowShell3 = addGlowShell(1.45, 0.06, 0xFFBE7B);
        const glowShell4 = addGlowShell(1.75, 0.03, 0xFFD4A8);

        /* ── Hot-spot highlights on the sphere ────────────── */
        const hlGeo = new THREE.SphereGeometry(0.28, 24, 24);
        const hl1 = new THREE.Mesh(hlGeo, new THREE.MeshBasicMaterial({
            color: 0xFFBE7B, transparent: true, opacity: 0.55, depthWrite: false,
        }));
        hl1.position.set(-0.2, 0.25, 0.6);
        scene.add(hl1);

        const hl2 = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 16, 16),
            new THREE.MeshBasicMaterial({
                color: 0xFFD4A8, transparent: true, opacity: 0.35, depthWrite: false,
            })
        );
        hl2.position.set(0.35, -0.15, 0.55);
        scene.add(hl2);

        /* ── Orbiting rings — glowing orange ──────────────── */
        function createRing(radius, tube, color, emissiveIntensity, tiltX, tiltZ) {
            const geo = new THREE.TorusGeometry(radius, tube, 8, 128);
            const mat = new THREE.MeshStandardMaterial({
                color,
                emissive: color,
                emissiveIntensity,
                roughness: 0.15,
                metalness: 0.9,
                transparent: true,
                opacity: 0.85,
            });
            const mesh = new THREE.Mesh(geo, mat);
            const group = new THREE.Group();
            group.rotation.set(tiltX, 0, tiltZ);
            group.add(mesh);
            scene.add(group);
            return mesh;
        }

        const ring1 = createRing(1.45, 0.024, 0xF87217, 0.9, 0.25, 0.1);
        const ring2 = createRing(1.85, 0.020, 0xFF9A4E, 0.7, 1.1, 0.4);
        const ring3 = createRing(1.95, 0.016, 0xFFBE7B, 0.6, -0.3, 1.55);

        /* ── Orbiting energy dots ────────────────────────── */
        function createDot(radius, color, tiltX, tiltZ, dotSize) {
            const geo = new THREE.SphereGeometry(dotSize, 12, 12);
            const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95 });
            const mesh = new THREE.Mesh(geo, mat);
            // Glow halo around each dot
            const haloGeo = new THREE.SphereGeometry(dotSize * 3, 8, 8);
            const haloMat = new THREE.MeshBasicMaterial({
                color, transparent: true, opacity: 0.2, depthWrite: false,
            });
            const halo = new THREE.Mesh(haloGeo, haloMat);
            const group = new THREE.Group();
            group.rotation.set(tiltX, 0, tiltZ);
            group.add(mesh);
            group.add(halo);
            scene.add(group);
            return { mesh, halo, radius };
        }

        const dot1 = createDot(1.52, 0xFFD4A8, 0.25, 0.1, 0.065);
        const dot2 = createDot(1.95, 0xF87217, 1.1, 0.4, 0.055);
        const dot3 = createDot(2.35, 0xFF9A4E, -0.3, 1.55, 0.048);

        /* ── Particle field ──────────────────────────────── */
        const particleCount = 120;
        const positions = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        for (let i = 0; i < particleCount; i++) {
            const phi = Math.acos(2 * Math.random() - 1);
            const theta = Math.random() * Math.PI * 2;
            const r = 1.2 + Math.random() * 1.0; // smaller radius to keep model within view
            positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = r * Math.cos(phi);
            sizes[i] = 0.015 + Math.random() * 0.025;
        }
        const particleGeo = new THREE.BufferGeometry();
        particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const particleMat = new THREE.PointsMaterial({
            color: 0xF87217,
            size: 0.03,
            transparent: true,
            opacity: 0.65,
            sizeAttenuation: true,
        });
        const particles = new THREE.Points(particleGeo, particleMat);
        scene.add(particles);

        /* ── Resize handling ─────────────────────────────── */
        function handleResize() {
            const w = container.clientWidth;
            const h = container.clientHeight;
            renderer.setSize(w, h);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        }
        handleResize();
        const resizeObs = new ResizeObserver(handleResize);
        resizeObs.observe(container);

        /* ── Animation loop ──────────────────────────────── */
        let frameId;
        function animate() {
            frameId = requestAnimationFrame(animate);
            const t = performance.now() * 0.001;

            // Orb breathing
            const orbScale = 1 + Math.sin(t * 1.2) * 0.035;
            orb.scale.setScalar(orbScale);
            orb.rotation.y = t * 0.15;

            // Pulsing emissive intensity
            orbMat.emissiveIntensity = 0.6 + Math.sin(t * 1.5) * 0.15;

            // Glow shell pulses (staggered)
            glowShell1.scale.setScalar(1.0 + Math.sin(t * 0.9) * 0.05);
            glowShell1.material.opacity = 0.22 + Math.sin(t * 1.3) * 0.06;
            glowShell2.scale.setScalar(1.0 + Math.sin(t * 0.7 + 0.5) * 0.06);
            glowShell2.material.opacity = 0.10 + Math.sin(t * 1.1 + 0.5) * 0.04;
            glowShell3.scale.setScalar(1.0 + Math.sin(t * 0.5 + 1.0) * 0.07);
            glowShell3.material.opacity = 0.05 + Math.sin(t * 0.9 + 1.0) * 0.03;
            glowShell4.scale.setScalar(1.0 + Math.sin(t * 0.4 + 1.5) * 0.08);

            // Light intensity pulse
            glow1.intensity = 3.5 + Math.sin(t * 1.2) * 1.0;
            glow2.intensity = 2.0 + Math.sin(t * 0.8 + 1) * 0.8;

            // Rings rotate
            ring1.rotation.y = t * 0.42;
            ring2.rotation.y = t * -0.28;
            ring3.rotation.y = t * 0.18;

            // Dots orbit with halo following
            function moveDot(dotObj, speed, phaseOffset) {
                const angle = t * speed + phaseOffset;
                const x = Math.cos(angle) * dotObj.radius;
                const z = Math.sin(angle) * dotObj.radius;
                dotObj.mesh.position.set(x, 0, z);
                dotObj.halo.position.set(x, 0, z);
                dotObj.halo.material.opacity = 0.15 + Math.sin(t * 2 + phaseOffset) * 0.08;
            }
            moveDot(dot1, 0.42, 0);
            moveDot(dot2, -0.28, 2);
            moveDot(dot3, 0.18, 4);

            // Particles slowly rotate
            particles.rotation.y = t * 0.03;
            particles.rotation.x = Math.sin(t * 0.02) * 0.05;

            // Camera drift
            camera.position.x = Math.sin(t * 0.1) * 0.55;
            camera.position.y = Math.sin(t * 0.05) * 0.2;
            camera.lookAt(0, 0, 0);

            renderer.render(scene, camera);
        }
        animate();

        /* ── Cleanup ─────────────────────────────────────── */
        return () => {
            cancelAnimationFrame(frameId);
            resizeObs.disconnect();
            renderer.dispose();
            if (container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
            scene.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                    else obj.material.dispose();
                }
            });
        };
    }, []);

    return (
        <div
            ref={mountRef}
            className={className}
            style={{
                width: '100%',
                height: '100%',
                minHeight: '420px',
                background: 'transparent',
                ...style,
            }}
        />
    );
}
